/**
 * TextBlock.tsx
 *
 * A rich-text journey component whose heading and body content are fully
 * editable in the no-code builder (CKEditor-style toolbar) and rendered as
 * safe HTML prose in the live journey.
 *
 * Builder mode  → contentEditable WYSIWYG editor with a full formatting toolbar
 * Journey mode  → dangerouslySetInnerHTML read-only prose
 *
 * Value shape:
 *   { heading?: string; headingHtml?: string; content?: string }
 *   where `content` and `headingHtml` are HTML strings
 */

import React, {
  useRef,
  useEffect,
  useCallback,
  useState,
} from 'react';

// ─── Types ────────────────────────────────────────────────────────────────────

interface TextBlockValue {
  heading?: string;
  headingHtml?: string;
  content?: string;
}

interface TextBlockProps {
  value?: TextBlockValue;
  onChange?: (val: TextBlockValue) => void;
  disabled?: boolean;
  isBuilderMode?: boolean;
}

// ─── Toolbar config ───────────────────────────────────────────────────────────

const FONT_SIZES = [
  { label: '10px', value: '1' },
  { label: '12px', value: '2' },
  { label: '14px', value: '3' },
  { label: '16px', value: '4' },
  { label: '18px', value: '5' },
  { label: '24px', value: '6' },
  { label: '32px', value: '7' },
];

const PRESET_COLORS = [
  '#000000', '#434343', '#666666', '#999999', '#b7b7b7', '#cccccc', '#d9d9d9', '#ffffff',
  '#ff0000', '#ff4500', '#ff9900', '#ffff00', '#00ff00', '#00ffff', '#4a86e8', '#9900ff',
  '#e06666', '#f6b26b', '#ffd966', '#93c47d', '#76a5af', '#6fa8dc', '#8e7cc3', '#c27ba0',
  '#cc0000', '#e69138', '#f1c232', '#6aa84f', '#45818e', '#3d85c8', '#674ea7', '#a64d79',
  '#FFD700', '#C0C0C0', '#B8860B', '#F5F5DC', '#FFFACD', '#FFF8DC', '#FAEBD7', '#FAF0E6',
];

// ─── Small utility components ─────────────────────────────────────────────────

const Divider: React.FC = () => (
  <span className="inline-block w-px h-5 bg-gray-300 mx-1 self-center" />
);

interface ToolBtnProps {
  title: string;
  active?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}

const ToolBtn: React.FC<ToolBtnProps> = ({ title, active, onClick, children }) => (
  <button
    type="button"
    title={title}
    onMouseDown={(e) => { e.preventDefault(); onClick(); }}
    className={[
      'inline-flex items-center justify-center w-7 h-7 rounded text-sm transition-colors select-none',
      active
        ? 'bg-gray-800 text-white'
        : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900',
    ].join(' ')}
  >
    {children}
  </button>
);

// ─── Color Picker Popover ─────────────────────────────────────────────────────

const POPOVER_W = 172;

interface ColorPickerProps {
  title: string;
  icon: React.ReactNode;
  currentColor: string;
  onSelect: (color: string) => void;
}

const ColorPicker: React.FC<ColorPickerProps> = ({ title, icon, currentColor, onSelect }) => {
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState(currentColor || '#000000');
  const [popPos, setPopPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const btnRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (
        popRef.current && !popRef.current.contains(e.target as Node) &&
        btnRef.current && !btnRef.current.contains(e.target as Node)
      ) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    const viewportW = window.innerWidth;
    const left = rect.left + POPOVER_W > viewportW
      ? Math.max(4, viewportW - POPOVER_W - 4)
      : rect.left;
    setPopPos({ top: rect.bottom + 4, left });
    setOpen(o => !o);
  };

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        title={title}
        onMouseDown={handleToggle}
        className="inline-flex flex-col items-center justify-center w-7 h-7 rounded text-sm text-gray-700 hover:bg-gray-100 transition-colors select-none"
      >
        <span className="leading-none">{icon}</span>
        <span
          className="w-4 h-1 rounded-sm mt-0.5"
          style={{ backgroundColor: currentColor || '#000000' }}
        />
      </button>

      {open && (
        <div
          ref={popRef}
          className="fixed z-[9999] bg-white border border-gray-200 rounded-lg shadow-2xl p-2"
          style={{ top: popPos.top, left: popPos.left, width: POPOVER_W }}
          onMouseDown={(e) => e.preventDefault()}
        >
          <div className="grid grid-cols-8 gap-0.5 mb-2">
            {PRESET_COLORS.map(c => (
              <button
                key={c}
                type="button"
                title={c}
                onMouseDown={(e) => { e.preventDefault(); onSelect(c); setOpen(false); }}
                className="w-4 h-4 rounded-sm border border-gray-200 hover:scale-110 transition-transform"
                style={{ backgroundColor: c }}
              />
            ))}
          </div>
          <div className="flex items-center gap-1 border-t border-gray-100 pt-2">
            <input
              type="color"
              value={custom}
              onChange={(e) => setCustom(e.target.value)}
              className="w-6 h-6 rounded cursor-pointer border border-gray-200 p-0 flex-shrink-0"
            />
            <span className="text-xs text-gray-500 flex-1 truncate">Custom</span>
            <button
              type="button"
              onMouseDown={(e) => { e.preventDefault(); onSelect(custom); setOpen(false); }}
              className="text-xs bg-gray-800 text-white px-1.5 py-1 rounded hover:bg-gray-700 flex-shrink-0"
            >
              OK
            </button>
          </div>
        </div>
      )}
    </>
  );
};

// ─── Link Dialog ──────────────────────────────────────────────────────────────

interface LinkDialogProps {
  selectedText: string;
  onInsert: (url: string, text: string) => void;
  onClose: () => void;
}

const LinkDialog: React.FC<LinkDialogProps> = ({ selectedText, onInsert, onClose }) => {
  const [url, setUrl] = useState('https://');
  const [text, setText] = useState(selectedText);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-2xl p-5 w-80 space-y-3">
        <h3 className="font-semibold text-gray-800 text-sm">Insert Link</h3>
        <div className="space-y-1">
          <label className="text-xs text-gray-500">Display text</label>
          <input
            type="text"
            value={text}
            onChange={e => setText(e.target.value)}
            className="w-full border border-gray-200 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            placeholder="Link text"
          />
        </div>
        <div className="space-y-1">
          <label className="text-xs text-gray-500">URL</label>
          <input
            type="url"
            value={url}
            onChange={e => setUrl(e.target.value)}
            className="w-full border border-gray-200 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            placeholder="https://example.com"
            autoFocus
          />
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-xs text-gray-600 bg-gray-100 hover:bg-gray-200 rounded"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => { if (url) { onInsert(url, text || url); onClose(); } }}
            className="px-3 py-1.5 text-xs text-white bg-blue-600 hover:bg-blue-700 rounded"
          >
            Insert
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Rich Text Toolbar ────────────────────────────────────────────────────────

interface ToolbarProps {
  editorRef: React.RefObject<HTMLDivElement>;
  onContentChange: () => void;
  disabled: boolean;
  standalone?: boolean;
}

const RichToolbar: React.FC<ToolbarProps> = ({ editorRef, onContentChange, disabled, standalone }) => {
  const [activeFormats, setActiveFormats] = useState<Set<string>>(new Set());
  const [fontSize, setFontSize] = useState('3');
  const [textColor, setTextColor] = useState('#000000');
  const [bgColor, setBgColor] = useState('#ffffff');
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [savedRange, setSavedRange] = useState<Range | null>(null);

  const refreshFormats = useCallback(() => {
    const formats = new Set<string>();
    const cmds = ['bold', 'italic', 'underline', 'strikeThrough',
      'justifyLeft', 'justifyCenter', 'justifyRight', 'justifyFull',
      'insertUnorderedList', 'insertOrderedList'];
    cmds.forEach(cmd => {
      try { if (document.queryCommandState(cmd)) formats.add(cmd); } catch (_) { /* noop */ }
    });
    setActiveFormats(formats);

    try {
      const fv = document.queryCommandValue('fontSize');
      if (fv) setFontSize(fv);
    } catch (_) { /* noop */ }

    try {
      const fc = document.queryCommandValue('foreColor');
      if (fc) {
        const m = fc.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
        if (m) {
          const hex = '#' + [m[1], m[2], m[3]]
            .map(n => parseInt(n).toString(16).padStart(2, '0')).join('');
          setTextColor(hex);
        } else if (fc.startsWith('#')) {
          setTextColor(fc);
        }
      }
    } catch (_) { /* noop */ }
  }, []);

  useEffect(() => {
    document.addEventListener('selectionchange', refreshFormats);
    return () => document.removeEventListener('selectionchange', refreshFormats);
  }, [refreshFormats]);

  const exec = useCallback((cmd: string, value?: string) => {
    editorRef.current?.focus();
    try { document.execCommand(cmd, false, value ?? undefined); } catch (_) { /* noop */ }
    onContentChange();
    refreshFormats();
  }, [editorRef, onContentChange, refreshFormats]);

  const saveSelection = (): string => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      setSavedRange(sel.getRangeAt(0).cloneRange());
      return sel.toString();
    }
    return '';
  };

  const restoreSelection = () => {
    if (savedRange) {
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(savedRange);
    }
  };

  const handleInsertLink = (url: string, text: string) => {
    editorRef.current?.focus();
    restoreSelection();
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      const range = sel.getRangeAt(0);
      if (!range.collapsed) range.deleteContents();
      const a = document.createElement('a');
      a.href = url;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      a.textContent = text;
      a.style.color = '#2563eb';
      a.style.textDecoration = 'underline';
      range.insertNode(a);
      range.setStartAfter(a);
      range.collapse(true);
      sel.removeAllRanges();
      sel.addRange(range);
    }
    onContentChange();
  };

  if (disabled) return null;

  const wrapperClass = standalone
    ? 'flex flex-wrap items-center gap-0.5 px-2 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-xs select-none mb-1'
    : 'flex flex-wrap items-center gap-0.5 px-2 py-1.5 bg-gray-50 border border-b-0 border-gray-200 rounded-t-lg text-xs select-none';

  return (
    <>
      {showLinkDialog && (
        <LinkDialog
          selectedText={savedRange ? savedRange.toString() : ''}
          onInsert={handleInsertLink}
          onClose={() => setShowLinkDialog(false)}
        />
      )}

      <div className={wrapperClass}>

        <select
          title="Font size"
          value={fontSize}
          onMouseDown={(e) => e.stopPropagation()}
          onChange={(e) => exec('fontSize', e.target.value)}
          className="h-7 px-1 text-xs border border-gray-200 rounded bg-white text-gray-700 focus:outline-none hover:border-gray-400 cursor-pointer"
        >
          {FONT_SIZES.map(s => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>

        <Divider />

        <ToolBtn title="Bold (Ctrl+B)" active={activeFormats.has('bold')} onClick={() => exec('bold')}>
          <strong className="font-black">B</strong>
        </ToolBtn>
        <ToolBtn title="Italic (Ctrl+I)" active={activeFormats.has('italic')} onClick={() => exec('italic')}>
          <em className="italic font-serif">I</em>
        </ToolBtn>
        <ToolBtn title="Underline (Ctrl+U)" active={activeFormats.has('underline')} onClick={() => exec('underline')}>
          <span className="underline">U</span>
        </ToolBtn>
        <ToolBtn title="Strikethrough" active={activeFormats.has('strikeThrough')} onClick={() => exec('strikeThrough')}>
          <span className="line-through">S</span>
        </ToolBtn>

        <Divider />

        <ColorPicker
          title="Text color"
          icon={<span className="font-bold text-sm leading-none">A</span>}
          currentColor={textColor}
          onSelect={(c) => { setTextColor(c); exec('foreColor', c); }}
        />
        <ColorPicker
          title="Highlight color"
          icon={
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <rect x="1" y="7" width="10" height="4" rx="1" fill="currentColor" opacity="0.5"/>
              <path d="M4 7L6 2L8 7" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          }
          currentColor={bgColor}
          onSelect={(c) => { setBgColor(c); exec('hiliteColor', c); }}
        />

        <Divider />

        <ToolBtn title="Align left" active={activeFormats.has('justifyLeft')} onClick={() => exec('justifyLeft')}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <line x1="1" y1="3" x2="13" y2="3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            <line x1="1" y1="7" x2="9" y2="7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            <line x1="1" y1="11" x2="11" y2="11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        </ToolBtn>
        <ToolBtn title="Align center" active={activeFormats.has('justifyCenter')} onClick={() => exec('justifyCenter')}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <line x1="1" y1="3" x2="13" y2="3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            <line x1="3" y1="7" x2="11" y2="7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            <line x1="2" y1="11" x2="12" y2="11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        </ToolBtn>
        <ToolBtn title="Align right" active={activeFormats.has('justifyRight')} onClick={() => exec('justifyRight')}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <line x1="1" y1="3" x2="13" y2="3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            <line x1="5" y1="7" x2="13" y2="7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            <line x1="3" y1="11" x2="13" y2="11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        </ToolBtn>
        <ToolBtn title="Justify" active={activeFormats.has('justifyFull')} onClick={() => exec('justifyFull')}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <line x1="1" y1="3" x2="13" y2="3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            <line x1="1" y1="7" x2="13" y2="7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            <line x1="1" y1="11" x2="13" y2="11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        </ToolBtn>

        <Divider />

        <ToolBtn title="Bullet list" active={activeFormats.has('insertUnorderedList')} onClick={() => exec('insertUnorderedList')}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <circle cx="2" cy="4" r="1.2" fill="currentColor"/>
            <line x1="5" y1="4" x2="13" y2="4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            <circle cx="2" cy="8" r="1.2" fill="currentColor"/>
            <line x1="5" y1="8" x2="13" y2="8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            <circle cx="2" cy="12" r="1.2" fill="currentColor"/>
            <line x1="5" y1="12" x2="13" y2="12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        </ToolBtn>
        <ToolBtn title="Numbered list" active={activeFormats.has('insertOrderedList')} onClick={() => exec('insertOrderedList')}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <text x="0.5" y="5.5" fontSize="4.5" fill="currentColor" fontFamily="monospace">1.</text>
            <line x1="5" y1="4" x2="13" y2="4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            <text x="0.5" y="9.5" fontSize="4.5" fill="currentColor" fontFamily="monospace">2.</text>
            <line x1="5" y1="8" x2="13" y2="8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            <text x="0.5" y="13.5" fontSize="4.5" fill="currentColor" fontFamily="monospace">3.</text>
            <line x1="5" y1="12" x2="13" y2="12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        </ToolBtn>

        <Divider />

        <ToolBtn title="Indent" onClick={() => exec('indent')}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <line x1="1" y1="3" x2="13" y2="3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            <line x1="5" y1="7" x2="13" y2="7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            <line x1="5" y1="11" x2="13" y2="11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            <path d="M1 6L3.5 7.5L1 9V6Z" fill="currentColor"/>
          </svg>
        </ToolBtn>
        <ToolBtn title="Outdent" onClick={() => exec('outdent')}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <line x1="1" y1="3" x2="13" y2="3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            <line x1="5" y1="7" x2="13" y2="7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            <line x1="5" y1="11" x2="13" y2="11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            <path d="M4 6L1.5 7.5L4 9V6Z" fill="currentColor"/>
          </svg>
        </ToolBtn>

        <Divider />

        <ToolBtn
          title="Insert link"
          onClick={() => {
            saveSelection();
            setShowLinkDialog(true);
          }}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M5.5 8.5a3.5 3.5 0 0 0 5 0l1.5-1.5a3.5 3.5 0 0 0-5-5L6.5 2.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
            <path d="M8.5 5.5a3.5 3.5 0 0 0-5 0L2 7a3.5 3.5 0 0 0 5 5l.5-.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
          </svg>
        </ToolBtn>
        <ToolBtn title="Remove link" onClick={() => exec('unlink')}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M5.5 8.5a3.5 3.5 0 0 0 5 0l1.5-1.5a3.5 3.5 0 0 0-5-5L6.5 2.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" opacity="0.35"/>
            <line x1="2" y1="2" x2="12" y2="12" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
          </svg>
        </ToolBtn>

        <Divider />

        <ToolBtn title="Clear formatting" onClick={() => exec('removeFormat')}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M3 3h8M6 3v8M4 11h4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
            <line x1="10" y1="8" x2="13" y2="11" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
            <line x1="13" y1="8" x2="10" y2="11" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
          </svg>
        </ToolBtn>
      </div>
    </>
  );
};

// ─── Pencil Icon ──────────────────────────────────────────────────────────────

const PencilIcon: React.FC<{ size?: number }> = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 14 14" fill="none">
    <path
      d="M9.5 2.5l2 2-7 7H2.5v-2l7-7z"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M8 4l2 2"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
    />
  </svg>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const TextBlock: React.FC<TextBlockProps> = ({
  value = {},
  onChange,
  disabled = false,
  isBuilderMode = false,
}) => {
  // Support both legacy `heading` (plain text) and new `headingHtml` (rich HTML)
  const headingHtml = value.headingHtml ?? (value.heading ?? '');
  const content = value.content ?? '';

  const headingEditorRef = useRef<HTMLDivElement>(null);
  const contentEditorRef = useRef<HTMLDivElement>(null);
  const headingInitialised = useRef(false);
  const contentInitialised = useRef(false);

  // Toggle for heading formatting toolbar — hidden by default
  const [showHeadingToolbar, setShowHeadingToolbar] = useState(false);
  // Toggle for content formatting toolbar — hidden by default
  const [showContentToolbar, setShowContentToolbar] = useState(false);

  // ── Sync heading HTML → editor DOM ──────────────────────────────────────────
  useEffect(() => {
    if (!headingEditorRef.current) return;
    if (!headingInitialised.current) {
      headingEditorRef.current.innerHTML = headingHtml || '';
      headingInitialised.current = true;
      return;
    }
    if (headingEditorRef.current.innerHTML !== headingHtml) {
      headingEditorRef.current.innerHTML = headingHtml || '';
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headingHtml]);

  // ── Sync content HTML → editor DOM ──────────────────────────────────────────
  useEffect(() => {
    if (!contentEditorRef.current) return;
    if (!contentInitialised.current) {
      contentEditorRef.current.innerHTML = content || '<p><br></p>';
      contentInitialised.current = true;
      return;
    }
    if (contentEditorRef.current.innerHTML !== content) {
      contentEditorRef.current.innerHTML = content || '<p><br></p>';
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content]);

  const handleContentChange = useCallback(() => {
    if (!contentEditorRef.current) return;
    const html = contentEditorRef.current.innerHTML;
    onChange?.({ ...value, content: html });
  }, [onChange, value]);

  const handleHeadingChange = useCallback(() => {
    if (!headingEditorRef.current) return;
    const html = headingEditorRef.current.innerHTML;
    const plain = headingEditorRef.current.innerText ?? '';
    onChange?.({ ...value, headingHtml: html, heading: plain });
  }, [onChange, value]);

  // ── Live journey: read-only styled prose ──────────────────────────────────
  if (!isBuilderMode) {
    return (
      <div className="space-y-3 py-1">
        {headingHtml ? (
          <h2
            className="text-xl font-bold text-gray-800 leading-snug [&_strong]:font-bold [&_em]:italic [&_u]:underline [&_s]:line-through [&_a]:text-blue-600 [&_a]:underline"
            dangerouslySetInnerHTML={{ __html: headingHtml }}
          />
        ) : null}
        {content ? (
          <div
            className="text-gray-700 text-sm leading-relaxed prose prose-sm max-w-none prose-a:text-blue-600 prose-a:underline prose-ul:list-disc prose-ul:pl-5 prose-ol:list-decimal prose-ol:pl-5"
            dangerouslySetInnerHTML={{ __html: content }}
          />
        ) : null}
        {!headingHtml && !content && (
          <p className="text-gray-400 text-sm italic">No content configured.</p>
        )}
      </div>
    );
  }

  // ── Builder canvas: rich WYSIWYG editor ───────────────────────────────────
  return (
    <div className="space-y-3 p-1">

      {/* ── Heading section ── */}
      <div className="space-y-1">
        {/* Label row with pencil/style toggle button */}
        <div className="flex items-center justify-between">
          <label className="text-xs font-semibold text-gray-400 uppercase tracking-wide select-none">
            Heading
          </label>
          <button
            type="button"
            title={showHeadingToolbar ? 'Hide heading formatting' : 'Format heading'}
            onClick={() => setShowHeadingToolbar(v => !v)}
            className={[
              'inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs transition-colors select-none',
              showHeadingToolbar
                ? 'bg-gray-800 text-white'
                : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100 border border-gray-200',
            ].join(' ')}
          >
            <PencilIcon size={11} />
            <span>Style</span>
          </button>
        </div>

        {/* Collapsible heading formatting toolbar — hidden by default */}
        {showHeadingToolbar && !disabled && (
          <RichToolbar
            editorRef={headingEditorRef as React.RefObject<HTMLDivElement>}
            onContentChange={handleHeadingChange}
            disabled={disabled}
            standalone
          />
        )}

        {/* Heading contentEditable field */}
        <div
          ref={headingEditorRef}
          contentEditable={!disabled}
          suppressContentEditableWarning
          onInput={handleHeadingChange}
          onBlur={handleHeadingChange}
          spellCheck
          className={[
            'w-full border border-gray-200 rounded-lg px-3 py-2',
            'text-base font-bold text-gray-800 leading-snug',
            'focus:outline-none focus:ring-2 focus:ring-intellend-green',
            '[&_strong]:font-bold [&_em]:italic [&_u]:underline [&_s]:line-through',
            '[&_a]:text-blue-600 [&_a]:underline',
            disabled ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : 'bg-white',
            'empty:before:content-[attr(data-placeholder)] empty:before:text-gray-400 empty:before:pointer-events-none empty:before:font-normal',
          ].join(' ')}
          data-placeholder="Enter heading…"
        />
      </div>

      {/* ── Content section ── */}
      <div className="space-y-0">
        {/* Label row with pencil/style toggle button */}
        <div className="flex items-center justify-between mb-1">
          <label className="text-xs font-semibold text-gray-400 uppercase tracking-wide select-none">
            Content
          </label>
          <button
            type="button"
            title={showContentToolbar ? 'Hide content formatting' : 'Format content'}
            onClick={() => setShowContentToolbar(v => !v)}
            className={[
              'inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs transition-colors select-none',
              showContentToolbar
                ? 'bg-gray-800 text-white'
                : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100 border border-gray-200',
            ].join(' ')}
          >
            <PencilIcon size={11} />
            <span>Style</span>
          </button>
        </div>

        {/* Collapsible content formatting toolbar — hidden by default */}
        {showContentToolbar && !disabled && (
          <RichToolbar
            editorRef={contentEditorRef as React.RefObject<HTMLDivElement>}
            onContentChange={handleContentChange}
            disabled={disabled}
            standalone
          />
        )}

        {/* Content editable area */}
        <div
          ref={contentEditorRef}
          contentEditable={!disabled}
          suppressContentEditableWarning
          onInput={handleContentChange}
          onBlur={handleContentChange}
          spellCheck
          className={[
            'min-h-[120px] w-full border border-gray-200 rounded-lg px-3 py-2',
            'text-sm text-gray-700 leading-relaxed',
            'focus:outline-none focus:ring-2 focus:ring-intellend-green',
            '[&_ul]:list-disc [&_ul]:pl-5',
            '[&_ol]:list-decimal [&_ol]:pl-5',
            '[&_a]:text-blue-600 [&_a]:underline',
            '[&_strong]:font-bold [&_em]:italic',
            disabled ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : 'bg-white',
            'empty:before:content-[attr(data-placeholder)] empty:before:text-gray-400 empty:before:pointer-events-none',
          ].join(' ')}
          data-placeholder="Start typing your content here…"
        />
      </div>
    </div>
  );
};

export default TextBlock;